import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { normalizeSupplierNif } from "@/lib/leasingLookup";

export interface ContractAccounts {
  costRecovery: string;
  financialCharge: string;
  insurance: string;
}

interface Props {
  contractId: string;
  companyId: string;
  supplierTaxId: string;
  /** Cuentas guardadas en el propio contrato (prioritarias) */
  contractAccounts: ContractAccounts;
  /** Valores por defecto del proveedor en esa empresa */
  defaults: ContractAccounts;
  onSaved: (next: { contractAccounts: ContractAccounts; defaults: ContractAccounts }) => void;
}

const ContractAccountsEditor = ({
  contractId, companyId, supplierTaxId, contractAccounts, defaults, onSaved,
}: Props) => {
  const { toast } = useToast();
  const [costRecovery, setCostRecovery] = useState(contractAccounts.costRecovery || defaults.costRecovery || "");
  const [financialCharge, setFinancialCharge] = useState(contractAccounts.financialCharge || defaults.financialCharge || "");
  const [insurance, setInsurance] = useState(contractAccounts.insurance || defaults.insurance || "");
  const [applyToSupplier, setApplyToSupplier] = useState(false);
  const [catalog, setCatalog] = useState<{ account: string; description: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const hasDefaults = !!(defaults.costRecovery || defaults.financialCharge || defaults.insurance);

  useEffect(() => {
    setCostRecovery(contractAccounts.costRecovery || defaults.costRecovery || "");
    setFinancialCharge(contractAccounts.financialCharge || defaults.financialCharge || "");
    setInsurance(contractAccounts.insurance || defaults.insurance || "");
  }, [
    contractAccounts.costRecovery, contractAccounts.financialCharge, contractAccounts.insurance,
    defaults.costRecovery, defaults.financialCharge, defaults.insurance,
  ]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase
        .from("company_account_catalog")
        .select("account, description")
        .eq("company_id", companyId);
      if (mounted) setCatalog((data || []) as { account: string; description: string }[]);
    })();
    return () => { mounted = false; };
  }, [companyId]);

  const describe = (acc: string) =>
    catalog.find((c) => c.account === acc.trim())?.description || "";

  const save = async () => {
    setSaving(true);
    try {
      const cr = costRecovery.trim();
      const fc = financialCharge.trim();
      const ins = insurance.trim();

      const { error: cErr } = await supabase
        .from("leasing_contracts")
        .update({
          cost_recovery_account: cr || null,
          financial_charge_account: fc || null,
          insurance_account: ins || null,
        })
        .eq("id", contractId);
      if (cErr) throw cErr;

      const nif = normalizeSupplierNif(supplierTaxId);
      let nextDefaults = defaults;

      if (applyToSupplier && nif) {
        const { data: existing } = await supabase
          .from("supplier_leasing_defaults")
          .select("id")
          .eq("company_id", companyId)
          .eq("supplier_tax_id", nif)
          .maybeSingle();
        if (existing?.id) {
          const { error } = await supabase
            .from("supplier_leasing_defaults")
            .update({ cost_recovery_account: cr, financial_charge_account: fc, insurance_account: ins })
            .eq("id", existing.id);
          if (error) throw error;
        } else if (cr || fc || ins) {
          const { error } = await supabase.from("supplier_leasing_defaults").insert({
            company_id: companyId,
            supplier_tax_id: nif,
            cost_recovery_account: cr,
            financial_charge_account: fc,
            insurance_account: ins,
          });
          if (error) throw error;
        }
        nextDefaults = { costRecovery: cr, financialCharge: fc, insurance: ins };
      }

      onSaved({
        contractAccounts: { costRecovery: cr, financialCharge: fc, insurance: ins },
        defaults: nextDefaults,
      });
      toast({ title: "Contrapartidas guardadas", description: "Se aplicarán al procesar nuevas facturas de este contrato." });
    } catch (e: any) {
      toast({ title: "No se pudo guardar", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const removeDefaults = async () => {
    const nif = normalizeSupplierNif(supplierTaxId);
    if (!nif) return;
    const { error } = await supabase
      .from("supplier_leasing_defaults")
      .delete()
      .eq("company_id", companyId)
      .eq("supplier_tax_id", nif);
    if (error) {
      toast({ title: "No se pudo eliminar", description: error.message, variant: "destructive" });
      return;
    }
    onSaved({
      contractAccounts: {
        costRecovery: costRecovery.trim(),
        financialCharge: financialCharge.trim(),
        insurance: insurance.trim(),
      },
      defaults: { costRecovery: "", financialCharge: "", insurance: "" },
    });
    toast({ title: "Valores por defecto eliminados", description: `Proveedor ${nif} en esta empresa.` });
  };

  const field = (
    id: string,
    label: string,
    hint: string,
    value: string,
    setValue: (v: string) => void,
  ) => (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Input
        id={id}
        list="financiacion-account-catalog"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={hint}
        className="h-8 text-xs font-mono"
      />
      <p className="text-[10px] text-muted-foreground truncate min-h-[14px]">{describe(value)}</p>
    </div>
  );

  return (
    <div className="rounded border bg-background p-3 space-y-3">
      <datalist id="financiacion-account-catalog">
        {catalog.map((c) => (
          <option key={c.account} value={c.account}>{c.description}</option>
        ))}
      </datalist>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-semibold">Contrapartidas de las facturas asignadas</h4>
        <div className="flex items-center gap-2">
          {hasDefaults && (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={removeDefaults}>
              Borrar valores del proveedor
            </Button>
          )}
          <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving}>
            {saving ? "Guardando…" : "Guardar cuentas"}
          </Button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {field("acc-cr", "Recuperación de coste (524)", "5240000", costRecovery, setCostRecovery)}
        {field("acc-fc", "Carga financiera (6623)", "66230010", financialCharge, setFinancialCharge)}
        {field("acc-ins", "Seguro (625)", "6250000", insurance, setInsurance)}
      </div>
      <div className="flex items-start gap-2">
        <Checkbox
          id={`apply-supplier-${contractId}`}
          checked={applyToSupplier}
          onCheckedChange={(v) => setApplyToSupplier(!!v)}
        />
        <Label htmlFor={`apply-supplier-${contractId}`} className="text-[11px] font-normal cursor-pointer leading-tight">
          Aplicar también como valores por defecto del proveedor {normalizeSupplierNif(supplierTaxId) || "—"} en esta empresa
          (se usarán en contratos que no tengan cuenta propia).
        </Label>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Las cuentas guardadas aquí mandan sobre cualquier otra regla al contabilizar las facturas de este contrato.
      </p>
    </div>
  );
};

export default ContractAccountsEditor;
